import type { BoxProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Button,
  Heading,
  Icon,
  system,
  transition,
} from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint/chakra';
import { Flex, HStack, SimpleGrid, SlideFade } from '@hover/blueprint/chakra';
import { resetFocus } from '@hover/blueprint/foundation';
import { iX } from '@hover/icons';
import React from 'react';

import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { PaletteCard } from 'src/modules/designStudio/components/DesignMenu/Menu/Templates/PaletteCard';
import { TemplateCard } from 'src/modules/designStudio/components/DesignMenu/Menu/Templates/TemplateCard';
import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import type { TemplateWithIcon } from 'src/modules/designStudio/helpers/data/TemplateStyles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

interface PaletterowProps extends FlexProps {
  palettes: TemplatePaletteConfig[];
  selectedPaletteIndex: number;
  onPaletteSelected: (index: number) => void;
  onPaletteRowShowMore: () => void;
  onPaletteRowCancel: () => void;
}

const PaletteRow = ({
  palettes,
  selectedPaletteIndex,
  onPaletteSelected,
  onPaletteRowCancel,
  onPaletteRowShowMore,
  ...props
}: PaletterowProps) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  if (!palettes || palettes.length === 0) return null;

  const PREVIEW_COUNT = 3;
  const previewPalettes = palettes.slice(0, PREVIEW_COUNT);
  const remainingCount = palettes.length - previewPalettes.length;

  return (
    <Flex
      direction="column"
      gap={100}
      justifyContent="stretch"
      marginTop={300}
      {...props}
    >
      <HStack justifyContent="space-between" mb="100">
        <Heading marginBottom="0" size={200}>
          {t('menu.template.paletteMiniRowHeader')}
        </Heading>
        <Button
          fill="minimal"
          label="Hide Palettes"
          onClick={onPaletteRowCancel}
          shape="circle"
          size="small"
        >
          <Icon color="secondary600" icon={iX} size="small" />
        </Button>
      </HStack>
      <Flex alignItems="center" flexDirection="row" flexWrap="nowrap" gap="300">
        {previewPalettes.map((palette, index) => (
          <PaletteCard
            alignSelf="stretch"
            flexGrow={1}
            flexShrink={1}
            isSelected={index === selectedPaletteIndex}
            isSmall
            key={palette.paletteName}
            maxWidth={{ base: '400', tablet: '100%' }}
            onClick={() => {
              onPaletteSelected(index);
            }}
            palette={palette}
            showName={false}
            width="100%"
          />
        ))}
        <system.button
          _hover={{ bg: 'neutral.300', color: 'neutral.900' }}
          bg="neutral.200"
          borderRadius="300"
          borderWidth="0"
          boxSizing="content-box"
          color="neutral.600"
          cursor="pointer"
          flexShrink={0}
          fontFamily="body"
          fontSize="200"
          fontWeight="bodyBold"
          height="200"
          onClick={onPaletteRowShowMore}
          p="200"
          width="200"
          {...transition.easing250('background-color')}
          sx={resetFocus({ shadow: 'ringPrimary' }, true)}
        >
          {remainingCount}+
        </system.button>
      </Flex>
    </Flex>
  );
};

export interface TemplatesViewProps extends BoxProps {
  closeMenu: () => void;
  templates: TemplateWithIcon[];
  palettes: TemplatePaletteConfig[];
  selectedTemplateIndex: number | undefined;
  selectedPaletteIndex: number;
  onTemplateSelected: (index: number) => void;
  onPaletteSelected: (index: number) => void;
  onPaletteRowShowMore: () => void;
  onPaletteRowCancel: () => void;
  isMiniPaletteRowVisible: boolean;
}

export const TemplatesView = ({
  closeMenu,
  templates,
  palettes,
  selectedTemplateIndex,
  selectedPaletteIndex,
  onTemplateSelected,
  onPaletteSelected,
  onPaletteRowCancel,
  onPaletteRowShowMore,
  isMiniPaletteRowVisible,
  ...props
}: TemplatesViewProps) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  return (
    <Box
      alignItems="stretch"
      bottom="0"
      flexDirection="column"
      height="100%"
      left="0"
      overflowX="hidden"
      padding="600"
      position="absolute"
      right="0"
      top="0"
      {...props}
    >
      <MenuHeader
        closeMenu={closeMenu}
        headerText={t('menu.template.title')!}
      />
      <Body alignSelf="center" fontWeight={100} mb={400} mt={0} size={400}>
        {t('menu.template.subtitle')}
      </Body>
      <Heading as="h2" marginBottom={300} size={300}>
        {t('menu.template.listHeader')}
      </Heading>
      <SimpleGrid
        columns={{ base: 2, tablet: undefined }}
        display={{ base: 'flex', tablet: 'grid' }}
        flexShrink={1}
        gap={400}
        margin="-100"
        minChildWidth="120px"
        minHeight="80px"
        overflowX={{ base: 'auto', tablet: 'visible' }}
        overflowY={{ base: 'visible', tablet: 'auto' }}
        padding="100"
      >
        {templates.map((template, index) => (
          <TemplateCard
            alignSelf="center"
            height={{ base: '100%', tablet: 'auto' }}
            imgPath={template.img}
            isSelected={selectedTemplateIndex === index}
            justifySelf="center"
            key={template.img}
            maxWidth="160px"
            onSelected={() => {
              onTemplateSelected(index);
            }}
            template={template.style}
            width="100%"
          />
        ))}
      </SimpleGrid>
      <Box
        alignItems="stretch"
        flexDirection="column"
        justifyContent="stretch"
        maxHeight={isMiniPaletteRowVisible ? '200px' : '0'}
        {...transition.easing250('max-height')}
      >
        <SlideFade in={isMiniPaletteRowVisible} offsetY="100%" unmountOnExit>
          <PaletteRow
            onPaletteRowCancel={onPaletteRowCancel}
            onPaletteRowShowMore={onPaletteRowShowMore}
            onPaletteSelected={onPaletteSelected}
            palettes={palettes}
            selectedPaletteIndex={selectedPaletteIndex}
            width="100%"
          />
        </SlideFade>
      </Box>
    </Box>
  );
};
